import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "renova360_cookie_consent";

export function getCookieConsent() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v ? JSON.parse(v) : null;
    } catch {
        return null;
    }
}

export function CookieBanner() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        // Show only if no decision has been recorded
        const consent = getCookieConsent();
        if (!consent) {
            // Small delay so the banner doesn't flash on initial paint
            const t = setTimeout(() => setOpen(true), 400);
            return () => clearTimeout(t);
        }
    }, []);

    const decide = (status) => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ status, ts: new Date().toISOString() })
            );
        } catch {
            /* ignore quota errors */
        }
        setOpen(false);
    };

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Consenso ai cookie"
            data-testid="cookie-banner"
            className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 animate-fade-up print:hidden"
        >
            <div className="mx-auto max-w-[920px] rounded-2xl bg-brand-navy text-white shadow-2xl border border-brand-gold/30 overflow-hidden">
                <div className="p-5 sm:p-6 lg:p-7 flex flex-col lg:flex-row gap-5 lg:items-center">
                    <div className="flex items-start gap-3 flex-1">
                        <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold">
                            <Cookie size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-xl text-white">Rispettiamo la tua privacy</div>
                            <p className="mt-1 text-[13px] sm:text-sm text-white/75 leading-6">
                                Utilizziamo solo cookie tecnici essenziali per autenticazione e pagamento sicuro.
                                Nessun cookie di profilazione o marketing.
                                Leggi la nostra{" "}
                                <a href="/cookie-policy" className="underline text-brand-gold hover:text-white" data-testid="cookie-banner-policy-link">
                                    Cookie Policy
                                </a>{" "}
                                e l’
                                <a href="/privacy-policy" className="underline text-brand-gold hover:text-white" data-testid="cookie-banner-privacy-link">
                                    Informativa sulla privacy
                                </a>.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2.5 lg:flex-col lg:items-stretch lg:w-[200px] shrink-0">
                        <button
                            type="button"
                            onClick={() => decide("rejected")}
                            data-testid="cookie-banner-reject"
                            className="flex-1 lg:flex-none px-5 py-2.5 rounded-full text-sm font-semibold border border-white/30 text-white hover:bg-white/10 transition-colors"
                        >
                            Rifiuta
                        </button>
                        <button
                            type="button"
                            onClick={() => decide("accepted")}
                            data-testid="cookie-banner-accept"
                            className="flex-1 lg:flex-none px-5 py-2.5 rounded-full text-sm font-semibold bg-brand-gold text-brand-navy hover:bg-brand-gold/90 transition-colors"
                        >
                            Accetta
                        </button>
                    </div>
                    <button
                        type="button"
                        aria-label="Chiudi"
                        onClick={() => decide("rejected")}
                        className="absolute top-3 right-3 lg:hidden text-white/60 hover:text-white"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}
